'use client'

import { useId } from 'react'

import { Anchor } from '@/components/client/seo/anchor'
import type { GlossaryLabels } from '@/lib/glossary/types'

import { useGlossaryPanel } from './use-glossary-panel'

export const GlossaryPanel = ({ labels }: { labels: GlossaryLabels }) => {
  const id = useId()
  const { panelRef, closeRef, state } = useGlossaryPanel()
  const term = state?.status === 'ready' ? state.term : null

  return (
    <div
      ref={panelRef}
      id={id}
      popover="auto"
      role="dialog"
      aria-labelledby={`${id}-title ${id}-term`}
      className="glossary-panel not-prose fixed m-auto max-h-[calc(100dvh-2rem)] w-[min(26rem,calc(100vw-2rem))] overflow-y-auto rounded-2xl bg-white p-6 text-navy shadow-[0_24px_48px_rgb(26_32_68/0.22)]"
      data-print-hidden
    >
      <div className="-mt-2 -mr-2 mb-2 flex items-center justify-between gap-3">
        <p id={`${id}-title`} className="m-0 text-[14px] leading-snug font-normal text-navy-80">
          {labels.title}
        </p>
        <button
          ref={closeRef}
          data-tracking="ignore"
          type="button"
          popoverTarget={id}
          popoverTargetAction="hide"
          aria-label={labels.close}
          className="flex size-[44px] shrink-0 cursor-pointer items-center justify-center rounded-lg text-navy-80 hover:bg-navy-5 hover:text-navy focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lapis"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            aria-hidden="true"
          >
            <path d="m6 6 12 12M6 18 18 6" />
          </svg>
        </button>
      </div>
      <p id={`${id}-term`} className="mt-0 mb-2 text-[20px] font-semibold">
        {term?.label ?? (state && 'label' in state ? state.label : '')}
      </p>
      <div aria-live="polite" aria-atomic="true">
        <p className="m-0 text-[16px] leading-relaxed text-navy-80">
          {term?.definition ?? (state?.status === 'error' ? labels.error : labels.loading)}
        </p>
        {state?.status === 'error' && (
          <button
            type="button"
            data-glossary-retry
            data-tracking="ignore"
            className="mt-3 min-h-11 cursor-pointer text-lapis underline"
          >
            {labels.retry}
          </button>
        )}
        {term?.disclaimer && <p className="mt-4 text-[16px] leading-relaxed text-navy-80">{term.disclaimer}</p>}
        {term?.reference && (
          <Anchor
            href={term.reference.href}
            data-tracking="ignore"
            rel="help"
            className="mt-3 inline-block text-[16px] text-lapis underline underline-offset-4"
          >
            {term.reference.label}
          </Anchor>
        )}
      </div>
      <p className="mt-5 mb-0 border-t border-navy-10 pt-4 text-[14px] leading-relaxed text-navy-80">{labels.note}</p>
    </div>
  )
}
